import { DslError, tokenize } from '@core/filters/path-filter/dsl/lexer';
import { DslParser } from '@core/filters/path-filter/dsl/parser';
import { printLiteral } from '@core/filters/path-filter/dsl/printer';
import { HoleKind, holeName, scanHoles } from '@core/filters/path-filter/placeholders';
import {
  FilterField,
  FilterVocabulary
} from '@core/filters/path-filter/vocabulary/vocabulary';
import {
  FILTER_OP_SHAPES,
  FilterLiteral,
  FilterOpName,
  LiteralKind,
  OpArgument,
  fieldPath,
  makeOp
} from '@core/filters/path-filter/wire';
import { isoToDatetimeLocal } from '@core/utils/recipe-parameter-schema.utils';

import { ComposerMode } from '../path-filter.models';

export type ValueEditorKind =
  | 'none'
  | 'bool'
  | 'enum'
  | 'reference'
  | 'scalar'
  | 'range'
  | 'list'
  | 'relative_date'
  | 'text'
  | 'texts'
  | 'label';

export type LabelShape = 'label_literal' | 'label_literals' | 'label_range' | 'label_text';

/** Flat on purpose: templates read its members without narrowing a union. */
export interface ValueEditor {
  kind: ValueEditorKind;
  literalKind: LiteralKind;
  multiple: boolean;
  values: readonly string[];
  entityType: string | null;
  labelShape: LabelShape | null;
}

const NONE: ValueEditor = {
  kind: 'none',
  literalKind: 'string',
  multiple: false,
  values: [],
  entityType: null,
  labelShape: null
};

export function primaryLiteralKind(field: FilterField, op: FilterOpName): LiteralKind {
  return (field.literalKinds[op] ?? []).find((kind) => kind !== 'null') ?? 'string';
}

/** `tags` are the registered tag names, which a tag operator picks from. */
export function valueEditorFor(
  field: FilterField | undefined,
  op: FilterOpName | null,
  tags: readonly string[] = []
): ValueEditor {
  if (!field || !op) return NONE;
  const literalKind = primaryLiteralKind(field, op);
  const base: ValueEditor = { ...NONE, literalKind };
  const values = field.enumValues ?? [];
  const reference = literalKind === 'uid' ? (field.uidEntityType ?? null) : null;
  const shape = FILTER_OP_SHAPES[op];
  switch (shape) {
    case 'none':
      return NONE;
    case 'literal':
      if (literalKind === 'bool') return { ...base, kind: 'bool' };
      if (reference) return { ...base, kind: 'reference', entityType: reference };
      if (values.length > 0 && literalKind === 'string')
        return { ...base, kind: 'enum', values };
      return { ...base, kind: 'scalar' };
    case 'range':
      return { ...base, kind: 'range' };
    case 'literals':
      if (reference)
        return { ...base, kind: 'reference', multiple: true, entityType: reference };
      if (values.length > 0 && literalKind === 'string') {
        return { ...base, kind: 'enum', multiple: true, values };
      }
      return { ...base, kind: 'list' };
    case 'relative_date':
      return { ...base, kind: 'relative_date' };
    case 'text':
      if (field.type === 'TAGS') return { ...base, kind: 'enum', values: tags };
      return { ...base, kind: 'text' };
    case 'texts':
      if (field.type === 'TAGS')
        return { ...base, kind: 'enum', multiple: true, values: tags };
      return { ...base, kind: 'texts' };
    default:
      return { ...base, kind: 'label', labelShape: shape };
  }
}

/** One literal in the DSL's own spelling, through the DSL's own checks; `null` when it refuses. */
function dslLiteral(text: string): FilterLiteral | null {
  try {
    const parser = new DslParser(tokenize(text), text);
    const literal = parser.parseLiteral();
    return parser.atEnd() ? literal : null;
  } catch (error) {
    if (error instanceof DslError) return null;
    throw error;
  }
}

/** What a `datetime-local` input gives: a time with no offset, read in the viewer's zone. */
const WALL_CLOCK = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;

function dateTimeLiteral(text: string): FilterLiteral | null {
  let stamp = text;
  if (WALL_CLOCK.test(text)) {
    // `Date` rolls an impossible day over into the next month, so the day is checked first.
    const day = dslLiteral(text.slice(0, 10));
    const time = new Date(text).getTime();
    if (day === null || Number.isNaN(time)) return null;
    stamp = new Date(time).toISOString();
  }
  const literal = dslLiteral(stamp);
  return literal !== null && literal !== 'null' && 'date_time' in literal ? literal : null;
}

export function parseLiteral(kind: LiteralKind, raw: string): FilterLiteral | null {
  const text = raw.trim();
  if (text === '') return null;
  switch (kind) {
    case 'string':
      return { string: text };
    case 'uid':
      return { uid: text };
    case 'date_time':
      return dateTimeLiteral(text);
    case 'number': {
      const literal = dslLiteral(text);
      if (literal === null || literal === 'null') return null;
      if ('number' in literal) return literal;
      return 'integer' in literal ? { number: literal.integer } : null;
    }
    case 'integer':
    case 'bool':
    case 'date':
    case 'duration':
    case 'mass':
    case 'length': {
      const literal = dslLiteral(text);
      return literal !== null && literal !== 'null' && kind in literal ? literal : null;
    }
    default:
      return null;
  }
}

/** `null` when any item fails to parse; `[]` for blank text, which leaves the condition incomplete. */
export function parseLiteralList(kind: LiteralKind, raw: string): FilterLiteral[] | null {
  const literals = raw
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
    .map((item) => parseLiteral(kind, item));
  return literals.every((literal) => literal !== null)
    ? (literals as FilterLiteral[])
    : null;
}

export type DurationUnit = 'seconds' | 'minutes' | 'hours' | 'days';

const UNIT_SECONDS: Record<DurationUnit, number> = {
  seconds: 1,
  minutes: 60,
  hours: 3600,
  days: 86400
};

const ISO_DURATION =
  /^P(?:(\d+(?:\.\d+)?)W)?(?:(\d+(?:\.\d+)?)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/;

/**
 * An ISO duration as its total length in the largest unit that divides it exactly. `null` for
 * anything else, months and years included: their length is not fixed, so no unit can hold them.
 */
export function splitDuration(iso: string): { amount: number; unit: DurationUnit } | null {
  const match = ISO_DURATION.exec(iso);
  if (!match || iso.endsWith('T') || iso === 'P') return null;
  const [weeks, days, hours, minutes, seconds] = match
    .slice(1)
    .map((part) => (part === undefined ? 0 : Number(part)));
  // Fractional parts summed in floating point miss an exact unit by a hair without the rounding.
  const total =
    Math.round(
      (weeks * 7 * 86400 + days * 86400 + hours * 3600 + minutes * 60 + seconds) * 1e6
    ) / 1e6;
  const unit = (['days', 'hours', 'minutes'] as const).find(
    (candidate) => total !== 0 && total % UNIT_SECONDS[candidate] === 0
  );
  const chosen = unit ?? 'seconds';
  return { amount: total / UNIT_SECONDS[chosen], unit: chosen };
}

export function joinDuration(amount: number, unit: DurationUnit): string {
  switch (unit) {
    case 'days':
      return `P${amount}D`;
    case 'hours':
      return `PT${amount}H`;
    case 'minutes':
      return `PT${amount}M`;
    default:
      return `PT${amount}S`;
  }
}

/** The text an input shows for a literal; `parseLiteral` reads it back. */
export function formatLiteral(literal: FilterLiteral | null | undefined): string {
  if (literal == null || literal === 'null') return '';
  if ('string' in literal) return literal.string;
  if ('uid' in literal) return literal.uid;
  if ('date_time' in literal)
    return isoToDatetimeLocal(literal.date_time) || literal.date_time;
  if ('date' in literal) return literal.date;
  if ('duration' in literal) return literal.duration;
  if ('integer' in literal) return String(literal.integer);
  if ('number' in literal) return String(literal.number);
  if ('bool' in literal) return String(literal.bool);
  return printLiteral(literal);
}

export type SlotSource = 'value' | 'parameter' | 'self_field' | 'self_label';

export interface SlotSwitches {
  parameterKind: HoleKind | null;
  self: boolean;
}

/**
 * The argument of `op` whose one value slot holds `text` — a `{{name}}` reference. `null` for
 * shapes with no single value slot (ranges, label ranges, operators without argument).
 */
export function holeArgument(
  op: FilterOpName,
  literalKind: LiteralKind,
  text: string,
  label = ''
): OpArgument | null {
  const literal = { [literalKind]: text } as unknown as FilterLiteral;
  switch (FILTER_OP_SHAPES[op]) {
    case 'literal':
      return literal;
    case 'literals':
      return [literal];
    case 'text':
      return text;
    case 'texts':
      return [text];
    case 'label_literal':
      return { label, value: literal };
    case 'label_literals':
      return { label, values: [literal] };
    case 'label_text':
      return { label, value: text };
    default:
      return null;
  }
}

export function slotSwitches(
  mode: ComposerMode,
  field: FilterField | undefined,
  op: FilterOpName | null,
  vocabulary: FilterVocabulary
): SlotSwitches {
  if (!field || !op) return { parameterKind: null, self: false };
  let parameterKind: HoleKind | null = null;
  if (mode === 'recipe' || mode === 'template') {
    // Ask the hole walker itself whether this slot may hold one, and of which kind.
    const probe = holeArgument(op, primaryLiteralKind(field, op), '{{probe}}', 'probe');
    if (probe !== null) {
      const scan = scanHoles({
        predicate: { path: fieldPath('probe'), op: makeOp(op, probe) }
      });
      parameterKind =
        scan.issues.length === 0 && scan.holes.length === 1 ? scan.holes[0].kind : null;
    }
  }
  const self =
    mode === 'compat' && vocabulary.allowsSelfRef && FILTER_OP_SHAPES[op] === 'literal';
  return { parameterKind, self };
}

function literalString(literal: unknown): string | null {
  if (!literal || typeof literal !== 'object') return null;
  const value = Object.values(literal)[0];
  return typeof value === 'string' ? value : null;
}

function soleText(items: readonly unknown[]): string | null {
  if (items.length !== 1) return null;
  return typeof items[0] === 'string' ? items[0] : literalString(items[0]);
}

/** The parameter name an argument's value slot refers to, if it holds `{{name}}`. */
export function holeOf(arg: OpArgument | null): string | null {
  if (arg === null) return null;
  let text: string | null;
  if (typeof arg === 'string') text = arg;
  else if (Array.isArray(arg)) text = soleText(arg);
  else if ('label' in arg) {
    if ('values' in arg) text = soleText(arg.values ?? []);
    else if ('value' in arg)
      text = typeof arg.value === 'string' ? arg.value : literalString(arg.value);
    else text = null;
  } else text = literalString(arg);
  return text === null ? null : holeName(text);
}

export function slotSource(arg: OpArgument | null): SlotSource {
  if (arg && typeof arg === 'object' && !Array.isArray(arg) && 'self' in arg) {
    return 'label' in arg.self ? 'self_label' : 'self_field';
  }
  return holeOf(arg) === null ? 'value' : 'parameter';
}
